/**
 * Keep destroyed drones visible during deploy targeting VFX (Kek, Enhanced Lion, etc.)
 * until the projectile reaches the drone slot.
 */

function resolveDroneOwnerPlayerId(droneZone, viewerUserId, gameState) {
  if (!gameState?.state) {
    throw new Error('resolveDroneOwnerPlayerId: gameState.state is required');
  }
  if (viewerUserId == null) {
    throw new Error('resolveDroneOwnerPlayerId: viewerUserId is required');
  }
  if (droneZone === 'your_drone') {
    return String(viewerUserId);
  }
  if (droneZone === 'opponent_drone') {
    const playerIds = gameState.players || Object.keys(gameState.state);
    const opponentId = playerIds.find((id) => String(id) !== String(viewerUserId));
    if (!opponentId) {
      throw new Error('resolveDroneOwnerPlayerId: opponent player id not found');
    }
    return String(opponentId);
  }
  throw new Error(`resolveDroneOwnerPlayerId: unsupported drone zone ${String(droneZone)}`);
}

function findRemovedDroneAtSlot(prevDrones, nextDrones, targetIndex) {
  if (!Array.isArray(prevDrones)) {
    return null;
  }
  const prevDrone = prevDrones[targetIndex];
  if (prevDrone?.instanceId) {
    const nextIds = new Set((nextDrones || []).filter(Boolean).map((d) => d.instanceId));
    if (!nextIds.has(prevDrone.instanceId)) {
      return prevDrone;
    }
    return null;
  }

  const nextIds = new Set((nextDrones || []).filter(Boolean).map((d) => d.instanceId));
  const removed = prevDrones.filter((d) => d && !nextIds.has(d.instanceId));
  if (removed.length === 1) {
    return removed[0];
  }
  return null;
}

/**
 * When a deploy projectile targets a drone slot and that drone vanished from authoritative
 * state, attach ghost metadata so BattleUI can keep rendering it until impact.
 */
export function enrichDeployDroneDestroyMeta(anim, prevState, nextState, viewerUserId) {
  const toZone = anim?.toSlot?.zone;
  if (toZone !== 'opponent_drone' && toZone !== 'your_drone') {
    return anim;
  }
  if (anim.isDroneDestroyAnimation && anim.ghostDrone) {
    return anim;
  }
  if (!prevState?.state || !nextState?.state) {
    return anim;
  }

  const targetIndex = anim.toSlot.index;
  if (targetIndex == null || targetIndex < 0) {
    return anim;
  }

  const ownerId = resolveDroneOwnerPlayerId(toZone, viewerUserId, prevState);
  const prevDrones = prevState.state[ownerId]?.drones;
  const nextDrones = nextState.state[ownerId]?.drones;
  const removedDrone = findRemovedDroneAtSlot(prevDrones, nextDrones, targetIndex);
  if (!removedDrone?.instanceId) {
    return anim;
  }

  return {
    ...anim,
    ghostDrone: { ...removedDrone, type: 'drone' },
    targetInstanceId: removedDrone.instanceId,
    isDroneDestroyAnimation: true,
    targetType: 'drone',
  };
}

/**
 * Merge live drones with deploy-ghost drones for a viewer-relative drone row.
 */
export function buildDroneSlotRenderEntries(drones, ghostEntries, droneZone) {
  const zoneGhosts = (ghostEntries || []).filter((g) => g.droneZone === droneZone);
  const occupiedIndices = new Set();
  const entries = [];

  (drones || []).forEach((drone, index) => {
    if (drone && !drone.markedForRemoval) {
      occupiedIndices.add(index);
      entries.push({ drone, index, isGhost: false });
    }
  });

  zoneGhosts.forEach((ghost) => {
    if (ghost.droneIndex == null) {
      return;
    }
    if (occupiedIndices.has(ghost.droneIndex)) {
      return;
    }
    const liveAtSlot = (drones || [])[ghost.droneIndex];
    if (liveAtSlot && !liveAtSlot.markedForRemoval) {
      return;
    }
    entries.push({
      drone: ghost.drone,
      index: ghost.droneIndex,
      isGhost: true,
    });
  });

  return entries.sort((a, b) => a.index - b.index);
}
